import { AuthFormFields, AuthFormProps } from './auth-form.types'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { Button } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { CheckBox } from '@dtx-company/shared-components/src/components/atoms/Checkbox/index'
import { CheckBoxWrapper, FormFields, StyledFieldSet, SubLinkWrapper, Wrapper } from './styles'
import { Domains } from '@dtx-company/true-common/src/types/domains'
import { Formik, FormikErrors } from 'formik'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { Input } from '@dtx-company/shared-components/src/components/atoms/Input/index'
import { LayoutProps, SpaceProps } from 'styled-system'
import { Link } from '@dtx-company/design-system/src'
import {
  MAX_EMAIL_CHARS,
  NEW_SIGN_UP_ERROR_MESSAGE,
  SIGN_IN_ERROR_MESSAGE,
  SIGN_UP_ERROR_MESSAGE,
  USER_ALREADY_EXIST_ERROR_MESSAGE
} from '@dtx-company/true-common/src/constants/auth'
import { MIN_PASSWORD_LENGTH } from '@dtx-company/app/shared/code/src/constants/flowcode'
import { MarketingPageRoutes, Routes } from '@dtx-company/true-common/src/constants/routes'
import { RootState } from '../../redux/types'
import { SLUG_RESERVED } from '@dtx-company/flow-codegen/src/page/queries'
import { SSORedirect } from './SSORedirect'
import { SocialAuth } from './social-auth'
import { SocialProvider } from '@dtx-company/true-common/src/types/socialProvider'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Spinner } from '@dtx-company/shared-components/src/components/atoms/Spinner/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { TextLink } from '../../utils/links'
import { UrlObject } from 'url'
import {
  auth_oldform_signin_emailpassword_started,
  auth_oldform_signin_sso_started,
  auth_oldform_signup_emailpassword_started
} from '@dtx-company/inter-app/src/constants/authHeapEvents'
import { clearAuthFormState, setAuthFormState } from '../../redux/slices/auth-form'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { fireHeapEvent } from '../../event-tracking/helpers'
import { gqlFetcher } from '../../services/gqlFetcher'
import { unsubscribeFromMarketingEmails, validateEmail } from '../../utils/notifications'
import { useAuthFormHeapEvents } from './auth-form.hooks'
import { useCohort } from '@dtx-company/inter-app/src/hooks/cohorts/useCohort'
import { useDispatch, useSelector } from 'react-redux'
import { useInviteState } from '../../hooks/useInviteState/useInviteState'
import { useIsEU } from '../../redux/selectors/currentSessionSelectors'
import { useLogin, useLoginExistingUser } from '../../hooks/auth/useLogin'
import { usePrelogin } from '../../hooks/auth/usePrelogin'
import { useRouter } from 'next/router'
import { useSignup } from '../../hooks/auth/useSignup'
import React, { useCallback, useEffect, useState } from 'react'
import accountEvents from '@dtx-company/inter-app/src/event-tracking/events/account'
import cohortsEvents from '@dtx-company/inter-app/src/event-tracking/events/cohorts'
import isEmail from 'validator/lib/isEmail'
import taggingEvents from '@dtx-company/inter-app/src/event-tracking/events/tagging'

const getCopy = (
  isSignIn: boolean
): {
  cta: string
  submitBtn: string
  defaultLinkHref: string
  defaultLinkText: string
} => ({
  cta: isSignIn ? 'Sign in with' : 'Sign up with',
  submitBtn: isSignIn ? 'Sign in with email' : 'Sign up with email',
  defaultLinkHref: isSignIn ? Routes.SIGN_UP : Routes.SIGN_IN,
  defaultLinkText: isSignIn ? 'Sign up' : 'Sign in'
})

const checkFlowpageSlug = async (variables: {
  slug: string
}): Promise<{ slugReserved: boolean }> => {
  return gqlFetcher(SLUG_RESERVED, variables, true)
}

export const SingleSignOnEnabled = (): JSX.Element => (
  <Box justifyContent="center" alignItems="center" mt="8px" data-testid="sso-label">
    <Icon src="/icons/lock-grey.svg" />
    <Text variant="body/medium" color="secondary.placeholder" pl="6px">
      Single sign-on enabled
    </Text>
  </Box>
)

function SignInUpToggle(props: {
  href?: UrlObject | string
  text: string
  onClick: () => void
  onSignInUpToggle?: () => void
}): JSX.Element {
  const inner = (
    <Text
      variant="body/small"
      color="primary.flowBlue"
      textAlign="center"
      data-testid="sublink-link"
    >
      {props.text}
    </Text>
  )
  if (props.href) {
    return (
      <TextLink href={props.href} onClick={() => props.onClick}>
        {inner}
      </TextLink>
    )
  }
  // Styles are to match how the link version looks, doesn't seem to be a
  // component for a button that looks like a link atm
  return (
    <button
      data-testid="toggle-auth-form-button"
      type="button"
      style={{
        display: 'flex',
        background: 'none',
        margin: 0,
        padding: 0,
        border: 0,
        cursor: 'pointer'
      }}
      onClick={props.onClick}
    >
      {inner}
    </button>
  )
}

const termsErrorCopy = 'You must accept the terms of use to continue.'

export function SocialLogins({
  values,
  setErrors,
  onSubmit,
  isSignIn = false,
  showFacebookBtn = false,
  slug = ''
}: {
  values: FormValues
  setErrors: (errors: FormikErrors<FormValues>) => void
} & AuthFormProps &
  LayoutProps &
  SpaceProps): JSX.Element {
  const { userClickedAuthFormAuthenticateWithSocial } = useAuthFormHeapEvents()
  const getCopyCb = useCallback(() => getCopy(isSignIn), [isSignIn])
  const { cta } = getCopyCb()
  const isSignupPage = useRouter().asPath.includes(Routes.SIGN_UP)
  const { name: cohortName } = useCohort()
  const [socialTermsError, setSocialTermsError] = useState<string>()

  const trackSignup = useCallback(() => {
    if (isSignupPage) {
      cohortsEvents.Revenue_SignupPage_SignedUp(cohortName ?? '')
    }
  }, [cohortName, isSignupPage])

  useEffect(() => {
    if (values.terms && socialTermsError) {
      setSocialTermsError(undefined)
    }
  }, [values.terms, socialTermsError])

  return (
    <>
      <SocialAuth
        isSignup={!isSignIn}
        checkTerms={(args: { quiet?: boolean } | void): boolean => {
          if (!values.terms && !isSignIn) {
            if (!args?.quiet) {
              setSocialTermsError(termsErrorCopy)
              setErrors({
                terms: termsErrorCopy
              })
            }
            return false
          }
          return true
        }}
        product={values?.slug || slug ? Domains.FLOWPAGE : Domains.FLOWCODE}
        showFacebookBtn={showFacebookBtn}
        // TODO: Rename onClick to handleSocialAuthSuccess
        onClick={(user, provider) => {
          trackSignup()
          onSubmit(user, provider)
        }}
        fireHeapEventOnClick={userClickedAuthFormAuthenticateWithSocial}
        cta={cta}
        margin={`30px 0 ${socialTermsError ? '15px' : ''}`}
      />
      {socialTermsError && (
        <Text margin="0 0 18px" textAlign="center" variant="body/small" color="red" role="alert">
          {socialTermsError}
        </Text>
      )}
    </>
  )
}

interface FormValues {
  slug: string
  noMarketingEmails: boolean
  terms: boolean
  email: string
  password: string
}

// TODO: break up comp
export const AuthForm: React.FC<AuthFormProps & LayoutProps & SpaceProps> = ({
  onSubmit,
  isSignIn = false,
  showFacebookBtn = false,
  slug = '',
  onToggleMode,
  ...rest
}: AuthFormProps & LayoutProps & SpaceProps): JSX.Element => {
  const initialAuthFormValues = useSelector((state: RootState) => state.appReducer.authFormReducer)
  const loginMethod = useLogin()
  const { loginExistingUser } = useLoginExistingUser()
  const getCopyCb = useCallback(() => getCopy(isSignIn), [isSignIn])
  const signupMethod = useSignup()
  const [error, setError] = useState('')
  const [activateTermsHint, setActivateTermsHint] = useState(false)
  const {
    userTypedInAuthFormEmailField,
    userTypedInAuthFormPasswordField,
    userFocusedAuthFormField,
    userClickedAuthFormForgotPassword,
    userClickedAuthFormSubmitButton,
    userCheckedAuthFormIAgreeToTermsAndConditions,
    userClickedAuthFormTermsAndConditionsLink
  } = useAuthFormHeapEvents()
  const [isSlugReserved, setSlugReserved] = React.useState(false)
  const { defaultLinkHref, defaultLinkText, submitBtn } = getCopyCb()
  const router = useRouter()
  const dispatch = useDispatch()
  const inviteState = useInviteState()
  //TODO: remove slug from this form, since it's never used - LD

  const slugRequired = false //!isSignIn && !slug

  const handleMouseClick = (): void => {
    setActivateTermsHint(false)
  }

  const { name: cohortName } = useCohort()
  const isSignupPage = useRouter().asPath.includes(Routes.SIGN_UP)
  const { emailIsSSOEnabled, checkIfEmailSSOEnabled, startSSORedirect, ssoRedirectHtml } =
    usePrelogin()
  const trackSignup = useCallback(() => {
    if (isSignupPage) {
      cohortsEvents.Revenue_SignupPage_SignedUp(cohortName ?? '')
    }
  }, [cohortName, isSignupPage])

  const ssoDomain = inviteState?.ssoDomain
  const mode = emailIsSSOEnabled || ssoDomain ? 'sso' : 'standard'

  React.useEffect(() => {
    document.addEventListener('mousedown', handleMouseClick)
    return () => {
      document.removeEventListener('mousedown', handleMouseClick)
    }
  }, [])

  function handleSignInUpToggleClick(): void {
    setError('')
    if (!isSignIn) {
      taggingEvents.Revenue_SignUpPage_Clicked_SignInButton()
    }
    if (onToggleMode) {
      onToggleMode()
    } else {
      router.push({
        pathname: defaultLinkHref,
        query: router.query
      })
    }
  }

  const SocialLoginDivider = (): JSX.Element => {
    return (
      <Box mb="24px" display="flex" align-items="center" justify-content="space-around">
        <Box backgroundColor="#636565" flexGrow={1} height="1px" />
        <Text
          textTransform="uppercase"
          textAlign="center"
          m="0 10px"
          lineHeight="7px"
          color="#636565"
        >
          OR
        </Text>
        <Box backgroundColor="primary.black" flexGrow={1} height="1px" />
        <Box />
      </Box>
    )
  }

  const isEU = useIsEU()

  return (
    <>
      {/* used for tests to detect presence of sign in or signup form */}
      <span data-testid={isSignIn ? 'signin-form' : 'signup-form'}></span>
      <SSORedirect redirectFormHtml={ssoRedirectHtml} />
      <Formik
        initialValues={{
          slug: '',
          // We automatically opt users into marketing emails by having the noMarketingEmails checkbox unchecked,
          // For EU users we cannot auto opt them in so the checkbox should start as checked.
          noMarketingEmails: !!isEU,
          terms: false,
          email: initialAuthFormValues?.email || '',
          password: initialAuthFormValues?.password || ''
        }}
        validate={values => {
          const errors: { name?: string; email?: string; password?: string; slug?: string } = {}
          if (!values.email) {
            errors.email = 'Required'
          } else if (!isEmail(values.email)) {
            errors.email = 'Invalid email address'
          } else if (
            ssoDomain &&
            !values.email.toLowerCase().endsWith('@' + ssoDomain.toLowerCase())
          ) {
            errors.email = `Must use an email at ${ssoDomain}`
          }

          if (mode === 'standard') {
            if (!values.password) {
              errors.password = 'Required'
            }
            if (!isSignIn && values.password?.length < MIN_PASSWORD_LENGTH) {
              errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`
            }
          }
          if (!values.slug && slugRequired) {
            errors.slug = 'Required'
          }

          return errors
        }}
        onSubmit={async (values, { setSubmitting, setErrors }) => {
          try {
            if (mode === 'sso') {
              fireAnalyticsEvent(auth_oldform_signin_sso_started)
              setSubmitting(true)
              await startSSORedirect()
              return
            } else {
              if (!isSignIn) {
                const validate = await validateEmail(values.email)
                if (!validate.valid_email_address) {
                  setErrors({
                    email: validate?.suggested_correction
                      ? `Did you mean: ${validate?.suggested_correction}?`
                      : 'Invalid email address.  Please enter a valid email to create your account'
                  })
                  return
                }
                if (!values.terms) {
                  setError(termsErrorCopy)
                  setErrors({
                    terms: termsErrorCopy
                  })
                  return
                }
              }
              if (!isSignIn && slugRequired) {
                const { slugReserved } = await checkFlowpageSlug({ slug: values.slug })
                if (slugReserved) {
                  setSlugReserved(true)
                  return
                }
              }

              setError('')
              setSubmitting(true)

              if (isSignIn) {
                fireAnalyticsEvent(auth_oldform_signin_emailpassword_started)
              } else {
                fireAnalyticsEvent(auth_oldform_signup_emailpassword_started)
              }

              const handler = isSignIn ? loginMethod : signupMethod

              //TODO - remove values?.slug from this condition, since the form never shows the input, bc slugRequired is hardcoded to false - LD
              const flowpageSlug = values?.slug || slug
              const product = flowpageSlug ? Domains.FLOWPAGE : Domains.FLOWCODE
              const user = await handler({
                ...values,
                product
              })
              if (!user) {
                throw new Error('Error authenticating user')
              }
              await onSubmit(user, SocialProvider.PASSWORD, flowpageSlug)
              trackSignup()
              if (values.noMarketingEmails && user?.email) {
                accountEvents.userOptedOutOfMarketingEmails()
                await unsubscribeFromMarketingEmails(user.email)
              }
            }
          } catch (e) {
            let errorMessage: React.ReactNode = isSignIn
              ? SIGN_IN_ERROR_MESSAGE
              : SIGN_UP_ERROR_MESSAGE

            if (e.message === USER_ALREADY_EXIST_ERROR_MESSAGE) {
              const signInUser = await loginExistingUser(values.email, values.password)

              errorMessage = NEW_SIGN_UP_ERROR_MESSAGE

              const flowpageSlug = values?.slug || slug

              if (signInUser.user) {
                await onSubmit(signInUser.user, SocialProvider.PASSWORD, flowpageSlug)
                return
              }
            }

            setError(errorMessage || e.message)
            console.error(e)
          } finally {
            setSubmitting(false)
            dispatch(clearAuthFormState())
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleChange,
          handleBlur,
          handleSubmit,
          setErrors,
          isSubmitting
        }) => (
          <form onSubmit={handleSubmit}>
            <Wrapper padding="0 34px" display="block" {...rest}>
              <StyledFieldSet>
                {!ssoDomain && (
                  <>
                    <SocialLogins
                      values={values}
                      setErrors={setErrors}
                      onSubmit={onSubmit}
                      isSignIn={isSignIn}
                      showFacebookBtn={showFacebookBtn}
                      slug={slug}
                    />
                    <SocialLoginDivider />
                  </>
                )}

                <FormFields>
                  {slugRequired && (
                    <Box flexDirection="column">
                      <Input
                        placeholder="Username"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.slug}
                        error={!!errors.slug && touched.slug}
                        helperText={errors.slug && touched.slug ? errors.slug : ''}
                        inputProps={{
                          name: 'slug'
                        }}
                        minWidth="100%"
                        type="text"
                        startAdornment={
                          <Text
                            pt="2px"
                            pl="16px"
                            color="primary.grey"
                            fontSize={[2, 2]}
                            variant="body/small"
                            mr="-4px"
                          >
                            {process.env.HOST + '/'}
                          </Text>
                        }
                      />
                      {isSlugReserved && <Text>slug reserved</Text>}
                    </Box>
                  )}
                  <Input
                    type="email"
                    onChange={e => {
                      const email = e.target.value?.toLowerCase()
                      checkIfEmailSSOEnabled(email)
                      setFieldValue(AuthFormFields.EMAIL, email)
                      userTypedInAuthFormEmailField()
                    }}
                    onFocus={() => {
                      userFocusedAuthFormField(isSignIn, 'email')
                      // Formik doesn't pick up autofill values so letting the
                      // user focus into the email field as a next-best option
                      // to set SSO state for them rather than having to
                      // change the value and change it back again
                      const email = values.email
                      checkIfEmailSSOEnabled(email)
                    }}
                    data-testid={`${isSignIn ? 'signin' : 'signup'}-email-input`}
                    onBlur={handleBlur}
                    value={values.email}
                    label="Email"
                    labelProps={{ marginBottom: '6px' }}
                    width="100%"
                    maxWidth="initial"
                    error={!!errors.email && touched.email}
                    helperText={errors.email && touched.email ? errors.email : ''}
                    inputProps={{
                      name: 'email',
                      maxLength: MAX_EMAIL_CHARS
                    }}
                  />

                  {mode === 'sso' ? (
                    <SingleSignOnEnabled />
                  ) : (
                    <Input
                      type="password"
                      onChange={e => {
                        setFieldValue(AuthFormFields.PASSWORD, e.target.value)
                        userTypedInAuthFormPasswordField()
                      }}
                      onFocus={() => {
                        userFocusedAuthFormField(isSignIn, 'password')
                      }}
                      onBlur={handleBlur}
                      value={values.password}
                      label="Password"
                      width="100%"
                      maxWidth="initial"
                      error={!!errors.password && touched.password}
                      helperText={errors.password && touched.password ? errors.password : ''}
                      inputProps={{
                        name: 'password'
                      }}
                      data-testid={`${isSignIn ? 'signin' : 'signup'}-password-input`}
                    />
                  )}

                  {mode === 'sso' && (router.query.flowpage as string) === 'true' && (
                    <Text
                      variant="body/small"
                      color="secondary.placeholder"
                      m="10px 12px 24px !important"
                      textAlign="center"
                      data-testid="sso-warning-losing-page"
                    >
                      Signing on with SSO will result in losing your page.
                    </Text>
                  )}
                  {!isSignIn && mode === 'standard' && (
                    <>
                      <Spacer mt="32px" />
                      <CheckBoxWrapper error={activateTermsHint || Boolean(errors.terms)}>
                        <CheckBox
                          checked={values.terms}
                          onChange={() => {
                            setError('')
                            setFieldValue('terms', !values.terms)
                            !values.terms && userCheckedAuthFormIAgreeToTermsAndConditions()
                          }}
                          data-testid="terms-checkbox"
                          label={
                            <>
                              I agree to the{' '}
                              <Link
                                href={MarketingPageRoutes.TERMS_OF_SERVICE}
                                target="_blank"
                                onClick={() => userClickedAuthFormTermsAndConditionsLink()}
                              >
                                <u>terms and conditions.</u>
                              </Link>
                            </>
                          }
                          labelProps={{ fontSize: '12px', paddingTop: '4px' }}
                        />
                      </CheckBoxWrapper>
                      <CheckBox
                        checked={values.noMarketingEmails}
                        onChange={() => {
                          taggingEvents.Revenue_SignUpPage_Clicked_MarketingOptOutCheckbox(
                            !values.noMarketingEmails
                          )
                          setFieldValue('noMarketingEmails', !values.noMarketingEmails)
                        }}
                        data-testid="marketing-emails-checkbox"
                        label={'I do not want to receive marketing emails from Flowcode.'}
                        labelProps={{ fontSize: '12px' }}
                      />
                    </>
                  )}
                </FormFields>
                {isSignIn && mode === 'standard' && (
                  <TextLink
                    padding="10px 0"
                    width="100%"
                    fontWeight="bold"
                    textAlign="right"
                    display="block"
                    color="primary.flowBlue"
                    href={Routes.FORGOT_PASSWORD}
                    onClick={userClickedAuthFormForgotPassword}
                    data-testid="forgot-password"
                  >
                    Forgot Password?
                  </TextLink>
                )}

                <Button
                  width="100%"
                  loading={isSubmitting}
                  type="submit"
                  data-testid="submit-btn"
                  margin="30px auto"
                  colorVariant={mode === 'sso' ? 'primary' : 'primaryBlack'}
                  sizeVariant="mobileFullWidth"
                  onClick={() => {
                    userClickedAuthFormSubmitButton({ sso: mode === 'sso' })
                    if (mode === 'standard') {
                      !isSignIn && taggingEvents.Revenue_SignUpPage_Clicked_SignUpButton('EMAIL')
                    }
                  }}
                >
                  {isSubmitting && mode === 'standard' ? (
                    <Spinner data-testid="submitting-spinner" height="24px" width="24px" />
                  ) : (
                    <Text
                      variant={mode === 'sso' ? 'body/medium' : 'body/small'}
                      fontWeight={mode === 'sso' ? 700 : 600}
                      color="primary.white"
                      data-testid="submit-button-text"
                    >
                      {mode === 'sso' && (isSubmitting ? 'Signing in...' : 'Sign in with SSO')}
                      {mode === 'standard' && submitBtn}
                    </Text>
                  )}
                </Button>

                {error && (
                  <Text
                    margin="16px 0"
                    textAlign="center"
                    variant="body/small"
                    color="red"
                    role="alert"
                  >
                    {error}
                  </Text>
                )}
                {!ssoDomain && (
                  <SubLinkWrapper>
                    <Text
                      variant="body/small"
                      mr="4px"
                      color="#646464"
                      textAlign="center"
                      marginBottom="10px"
                    >
                      {isSignIn ? "Don't have an account?" : 'Already have an account?'}
                    </Text>
                    <SignInUpToggle
                      text={defaultLinkText}
                      onClick={() => {
                        dispatch(
                          setAuthFormState({
                            email: values.email,
                            password: values.password
                          })
                        )
                        handleSignInUpToggleClick()
                      }}
                    />
                  </SubLinkWrapper>
                )}
                {ssoDomain && (
                  <SubLinkWrapper>
                    <Text
                      variant="body/small"
                      mr="4px"
                      color="#646464"
                      textAlign="center"
                      marginBottom="10px"
                    >
                      {isSignIn ? 'Want to sign in without SSO?' : 'Want to sign up without SSO?'}
                    </Text>
                    <SignInUpToggle
                      text={isSignIn ? 'Sign in' : 'Sign up'}
                      href={{
                        pathname: isSignIn ? Routes.SIGN_IN : Routes.SIGN_UP,
                        query: {
                          ...(() => {
                            // This will drop the query params that force sso mode.
                            // There shouldn't be any others, but if there are they'll
                            // be retained
                            const { org: _org, ssoDomain: _ssoDomain, ...rest } = router.query
                            return rest
                          })()
                        }
                      }}
                      onClick={() => {
                        fireHeapEvent('AuthForm_DisableSSOLink_Clicked')
                      }}
                    />
                  </SubLinkWrapper>
                )}
              </StyledFieldSet>
            </Wrapper>
          </form>
        )}
      </Formik>
    </>
  )
}
